import { createBrowserAgentConfig, validateConfig } from './config';
import type {
  NewRelicAttributes,
  NewRelicBrowser,
  NewRelicConfig,
  NewRelicErrorHandler,
} from './types';
import {
  formatContextAttributes,
  formatErrorToException,
} from './utils/error-utils';

async function loadNewRelicAgent(
  config: NewRelicConfig
): Promise<NewRelicBrowser | null> {
  const validatedConfig = validateConfig(config);
  if (!validatedConfig) {
    return createStubClient();
  }

  try {
    const { BrowserAgent } = await import(
      '@newrelic/browser-agent/loaders/browser-agent'
    );
    const agentConfig = createBrowserAgentConfig(validatedConfig);
    new BrowserAgent(agentConfig);

    if (typeof window !== 'undefined' && window.newrelic) {
      return createNewRelicClient();
    }

    console.warn(
      '[NewRelic] Browser agent loaded but newrelic global not available'
    );
    return createStubClient();
  } catch (error) {
    console.error('[NewRelic] Failed to load agent:', error);
    return createStubClient();
  }
}

function createNewRelicClient(): NewRelicBrowser {
  return {
    noticeError(
      error: Error | unknown,
      customAttributes?: NewRelicAttributes
    ): void {
      const formattedError = formatErrorToException(error);
      const attributes = formatContextAttributes(customAttributes);
      window.newrelic.noticeError(formattedError, attributes);
    },

    addPageAction(name: string, attributes?: NewRelicAttributes): void {
      const safeAttributes = formatContextAttributes(attributes);
      window.newrelic.addPageAction(name, safeAttributes);
    },

    setUserId(userId: string): void {
      window.newrelic.setUserId(userId);
    },

    setCustomAttribute(key: string, value: string | number): void {
      window.newrelic.setCustomAttribute(key, value);
    },

    setPageViewName(name: string): void {
      window.newrelic.setPageViewName(name);
    },

    interaction() {
      return window.newrelic.interaction();
    },

    setErrorHandler(handler: NewRelicErrorHandler): void {
      window.newrelic.setErrorHandler(handler);
    },

    setApplicationVersion(version: string): void {
      if (typeof window.newrelic.setApplicationVersion === 'function') {
        window.newrelic.setApplicationVersion(version);
      }
    },

    addRelease(appId: string, releaseName: string): void {
      if (typeof window.newrelic.addRelease === 'function') {
        window.newrelic.addRelease(appId, releaseName);
      }
    },
  };
}

function createStubClient(): NewRelicBrowser {
  return {
    noticeError: () => {},
    addPageAction: () => {},
    setUserId: () => {},
    setCustomAttribute: () => {},
    setPageViewName: () => {},
    interaction: () => null,
    setErrorHandler: () => {},
    setApplicationVersion: () => {},
    addRelease: () => {},
  };
}

export async function initNewRelicBrowser(
  config: NewRelicConfig
): Promise<NewRelicBrowser | null> {
  return loadNewRelicAgent(config);
}

export { initNewRelicBrowser as default };
export type {
  NewRelicBrowser,
  NewRelicConfig,
  NewRelicAttributes,
  NewRelicErrorHandler,
} from './types';
